import React, { useEffect, useState, useRef } from "react";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { DocumentUploadCard } from "../ui/DocumentUploadCard";
import { UploadedFiles } from "../UploadedFiles";
import { InvalidRequirementReason } from "../InvalidRequirementReason";
import { CancelToReview } from "../CancelToReview";
import { Status } from "../../../redux/interfaces/folder.types";
import { DocumentExamples } from "./DocumentExamples";
import { toast } from "sonner";
import { uploadFileRequirement } from "../../../utils/vault/uploadFileRequirement";
import { useUploadFileToVault } from "../../../hooks/vault/mutations/useUploadFileToVault";
import { useUpdateVaultFormValues } from "../../../hooks/vault/mutations/useUpdateVaultFormValues";
import type { UploadFile } from "antd/es/upload/interface";
import { BOLETA_REGISTRAL_MAX_FILE_SIZE_MB } from "../../../utils/vault/verificationUploadLimits";

interface Props {
  onRegisterSubmit?: (submitFn: (() => Promise<boolean>) | null) => void;
}

export const BoletaRegistral: React.FC<Props> = ({ onRegisterSubmit }) => {
  const [files, setFiles] = useState<UploadFile[]>([]);

  const dispatch = useAppDispatch();

  const { activeRequirement, activeVault, activeTab } = useAppSelector(
    (state) => state.vault
  );
  const uploadMutation = useUploadFileToVault(activeVault?.id);
  const updateFormValuesMutation = useUpdateVaultFormValues(activeVault?.id);

  const enabledInputs =
    activeRequirement?.status === Status.PendingUpload ||
    activeRequirement?.status === Status.Invalid;

  const isFormInitialized = useRef(false);

  const documentExamples = [
    {
      path: "/documents/boleta-registral.jpeg",
      description: "Boleta registral de la propiedad",
    },
  ];

  // Reset cuando cambia el requirement
  useEffect(() => {
    isFormInitialized.current = false;
  }, [activeRequirement?.id]);

  useEffect(() => {
    setFiles([]);
    isFormInitialized.current = true;
  }, [activeRequirement?.id]);

  const handleSubmit = async (): Promise<boolean> => {
    if (files.length === 0) {
      toast.error("Por favor sube un archivo");
      return false;
    }

    const file = files[0]?.originFileObj;
    const formFiles = [file];
    const documentTypeKey = "boleta_registral";
    const requirementFiles = activeRequirement?.files || [];
    const formNFiles = 1;
    const successMessage = "Boleta registral subida correctamente.";

    const messageApi = {
      success: (msg: string) => toast.success(msg),
      error: (msg: string) => toast.error(msg),
    };

    try {
      await uploadFileRequirement({
        uploadMutateAsync: uploadMutation.mutateAsync,
        updateFormValuesMutateAsync: updateFormValuesMutation.mutateAsync,
        dispatch,
        requirementFiles,
        documentType: documentTypeKey,
        formNFiles,
        formFiles,
        formValues: {},
        activeRequirement,
        activeVault,
        activeTab,
        successMessage,
        messageApi,
      });
      setFiles([]);
      return true;
      setFiles([]);
      return true;
    } catch (error) {
      console.error("Upload failed:", error);
      return false;
    }
  };

  // Register submit function with parent for external triggering
  const submitFnRef = useRef<(() => Promise<boolean>) | undefined>(undefined);
  submitFnRef.current = handleSubmit;

  useEffect(() => {
    if (!onRegisterSubmit) return;
    const fn = async () => submitFnRef.current?.() ?? false;
    onRegisterSubmit(fn);
    return () => onRegisterSubmit(null);
  }, [onRegisterSubmit]);

  return (
    <div className="w-full">
      {/* Header */}
      <div className="mb-8">
        <p className="text-gray-600">
          Sube la{" "}
          <span className="font-semibold text-gray-900">
            boleta registral con folio real
          </span>{" "}
          de la propiedad.
        </p>
      </div>

      {/* Invalid Requirement Reason — shown at the top so the rejection
          message is visible before the user scrolls through the form. */}
      {activeRequirement && (
        <InvalidRequirementReason requirement={activeRequirement} />
      )}

      {/* Two column layout - stack on mobile, 40/60 split on md+ */}
      <div className="grid grid-cols-1 md:grid-cols-[2fr_3fr] gap-6 md:gap-8">
        {/* Left Column - Description & Tips */}
        <div className="space-y-6 md:sticky md:top-0 md:self-start">
          {/* Description */}
          <div className="space-y-4">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
              Descripción
            </p>
            <div className="text-sm text-gray-600 space-y-3">
              <p>
                La boleta registral es un documento otorgado por el{" "}
                <span className="font-medium text-gray-900">
                  Registro Público de la Propiedad
                </span>{" "}
                a los inmuebles, comúnmente la puedes encontrar al final de las
                escrituras en una hoja tamaño carta.
              </p>
              <p className="font-medium text-gray-900">
                Asegúrate de que la boleta registral contenga el folio real.
              </p>
            </div>
          </div>

          {/* Alternative documents */}
          <div className="space-y-3">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
              Alternativas
            </p>
            <p className="text-sm text-gray-600">
              Si la boleta registral no contiene el folio real o no cuentas con
              ella, puedes adjuntar:
            </p>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-start gap-2">
                <span className="mt-1.5 w-1 h-1 rounded-full bg-gray-400 flex-shrink-0" />
                Certificado de Libertad de Gravamen
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-1.5 w-1 h-1 rounded-full bg-gray-400 flex-shrink-0" />
                Escrituras completas del inmueble (en un solo archivo)
              </li>
            </ul>
          </div>

          <DocumentExamples urls={documentExamples} />
        </div>

        {/* Right Column - File Upload */}
        <div className="space-y-6">
          <DocumentUploadCard
            name="boleta_registral"
            label="Boleta registral de la propiedad"
            type="both"
            enableCropper={false}
            maxSizeMB={BOLETA_REGISTRAL_MAX_FILE_SIZE_MB}
            onFileChange={setFiles}
            value={files}
          />

          {/* Uploaded Files */}
          <UploadedFiles />

          {/* Cancel to review (when already submitted) */}
          {!enabledInputs &&
            activeRequirement?.status === Status.PendingReview && (
              <CancelToReview requirement={activeRequirement} />
            )}
        </div>
      </div>
    </div>
  );
};
